import * as React from 'react';
import { LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { DisplayDate, DisplaySchedule, DisplayTime } from '@tock/time';
import { DisplayFees } from 'components/business/offering/DisplayFees';
import { IconCalendar, IconCash, IconClock, IconInvoice, IconMapMarker } from '@tock/icons';
import { IconFee } from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { OfferingPriceDescriptionDisplay } from 'components/business/offering/OfferingPriceDescriptionDisplay';
import { PriceRange, getPriceDistribution } from 'components/business/PriceRange';
import { Typography } from 'omakase';
import { consumer, core } from '@tock/coretypes';
import { isPaymentCardHoldExperience } from 'utils/priceUtils';
import Address from 'components/business/location/Address';
import IconCalendarLegacy from 'components/icons/svgr/IconCalendar';
import IconClockLegacy from 'components/icons/svgr/IconClock';
import IconMoney from 'components/icons/svgr/IconMoney';
import IconPin from 'components/icons/svgr/IconPin';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';

interface Props {
  schedule?: core.Schedule;
  eventFirstDatetime?: LocalDateTime;
  eventLastDatetime?: LocalDateTime;
  startDate?: LocalDate;
  startTime?: LocalTime;
  endTime?: LocalTime;
  price?: consumer.IOfferedPriceSummary;
  location?: consumer.ILocation;
  shouldDisplayAddress: boolean;
  isAtOneTime: boolean;
  isOnOneDate: boolean;
  offeringType: consumer.Offering.Type;
}

export default class OfferingDetailSummary extends PureBaseComponent<Props> {
  getDateDisplay() {
    const { eventFirstDatetime, eventLastDatetime, startDate } = this.props;
    if (
      eventFirstDatetime &&
      eventLastDatetime &&
      !eventFirstDatetime.toLocalDate().isEqual(eventLastDatetime.toLocalDate())
    ) {
      return (
        <>
          <DisplayDate displayMonthAs="short" dateValue={eventFirstDatetime} /> -{' '}
          <DisplayDate displayMonthAs="short" dateValue={eventLastDatetime} />
        </>
      );
    }

    if (startDate) {
      return <DisplayDate showWeekday displayMonthAs={'long'} dateValue={startDate} />;
    }

    if (eventFirstDatetime) {
      return <DisplayDate showWeekday displayMonthAs={'long'} dateValue={eventFirstDatetime} />;
    }

    return null;
  }

  getSingleDateTimeDisplay(): React.ReactNode {
    const { eventFirstDatetime, startTime, endTime, isAtOneTime } = this.props;
    const startTimeToUse = startTime
      ? startTime
      : eventFirstDatetime
        ? eventFirstDatetime.toLocalTime()
        : undefined;

    if (startTimeToUse) {
      return (
        <>
          {!isAtOneTime && <div>Multiple times, starting at</div>}
          <DisplayTime timeValue={startTimeToUse} />
          {!_.isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }
    return null;
  }

  getMultiDateTemplateTimeDisplay(): React.ReactNode {
    const { isAtOneTime, startTime, endTime } = this.props;
    if (isAtOneTime) {
      return (
        <>
          <DisplayTime timeValue={startTime} />
          {!_.isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }
    return <>Multiple times</>;
  }

  getPerTicketPriceDisplay(): React.ReactNode {
    const { price, offeringType } = this.props;

    const isCreditCardHold = isPaymentCardHoldExperience({ price });

    if (!price?.pricePerPerson || isCreditCardHold) {
      return null;
    }

    return (
      <div className="EventDetailSummary-item">
        <IconMigrationUtil
          legacyIcon={<IconMoney className="EventDetailSummary-icon" />}
          newIcon={<IconCash className="EventDetailSummary-icon" />}
        />
        <Typography variant="body02" component="span">
          <div>
            <PriceRange
              price={price as consumer.OfferedPriceSummary}
              type={offeringType}
              priceDistribution={getPriceDistribution(offeringType, price)}
            />
          </div>
        </Typography>
      </div>
    );
  }

  getMinRequiredSpendDisplay(): React.ReactNode {
    const { price } = this.props;

    if (!price || (price?.minRequiredSpend?.totalCents ?? 0) === 0 || !price?.priceDescription) {
      return null;
    }

    return (
      <div className="EventDetailSummary-item">
        <IconMigrationUtil
          legacyIcon={<IconMoney className="EventDetailSummary-icon" />}
          newIcon={<IconCash className="EventDetailSummary-icon" />}
        />
        <Typography variant="body02" component="span">
          <OfferingPriceDescriptionDisplay
            minRequiredSpendCents={price.minRequiredSpend?.totalCents ?? undefined}
            priceDescription={price.priceDescription}
            priceVariability={price.minRequiredSpend?.variability ?? undefined}
          />
        </Typography>
      </div>
    );
  }

  getFeesDisplay(): React.ReactNode {
    const { price } = this.props;
    return (
      <div className="EventDetailSummary-item" data-testid={'display-fee-value-testid'}>
        <IconMigrationUtil
          legacyIcon={<IconFee className="EventDetailSummary-icon" />}
          newIcon={<IconInvoice className="EventDetailSummary-icon" />}
        />
        <Typography variant="body02" component="span">
          <DisplayFees fees={price?.fees ?? []} />
        </Typography>
      </div>
    );
  }

  render() {
    const {
      schedule,
      eventFirstDatetime,
      startDate,
      price,
      location,
      shouldDisplayAddress,
      isOnOneDate,
    } = this.props;

    if (!eventFirstDatetime && !startDate && !price && !location) {
      return null;
    }

    const dateDisplay = this.getDateDisplay();
    const timeDisplay = isOnOneDate
      ? this.getSingleDateTimeDisplay()
      : this.getMultiDateTemplateTimeDisplay();

    return (
      <div className="EventDetailSummary">
        {!_.isNil(schedule) && (
          <div className="EventDetailSummary-item EventDetailSummary-item--alignTop">
            <IconMigrationUtil
              legacyIcon={<IconCalendarLegacy className="EventDetailSummary-icon" />}
              newIcon={<IconCalendar className="EventDetailSummary-icon" />}
            />
            <Typography variant="body02" component="span">
              <DisplaySchedule schedule={schedule} filterDatesEarlierThan={LocalDate.now()} />
            </Typography>
          </div>
        )}
        {_.isNil(schedule) && !_.isNil(dateDisplay) && (
          <div className="EventDetailSummary-item">
            <IconMigrationUtil
              legacyIcon={<IconCalendarLegacy className="EventDetailSummary-icon" />}
              newIcon={<IconCalendar className="EventDetailSummary-icon" />}
            />
            <Typography variant="body02" component="span">
              {dateDisplay}
            </Typography>
          </div>
        )}
        {timeDisplay && (
          <div className="EventDetailSummary-item">
            <IconMigrationUtil
              legacyIcon={<IconClockLegacy className="EventDetailSummary-icon" />}
              newIcon={<IconClock className="EventDetailSummary-icon" />}
            />
            <Typography variant="body02" component="span">
              {timeDisplay}
            </Typography>
          </div>
        )}
        {price?.priceDisplayStyle === 'MIN_REQUIRED_SPEND'
          ? this.getMinRequiredSpendDisplay()
          : this.getPerTicketPriceDisplay()}
        {price?.priceDisplayStyle === 'ITEMIZED' &&
          price?.fees?.length !== 0 &&
          this.getFeesDisplay()}
        {location && shouldDisplayAddress && (
          <div className="EventDetailSummary-item EventDetailSummary-item--alignTop">
            <IconMigrationUtil
              legacyIcon={<IconPin className="EventDetailSummary-icon" />}
              newIcon={<IconMapMarker className="EventDetailSummary-icon" />}
            />
            <Typography variant="body02" component="span">
              <Address
                locationName={oc(location).name() || undefined}
                address={oc(location).address() || undefined}
                address2={oc(location).address2() || undefined}
                city={oc(location).city() || undefined}
                state={oc(location).state() || undefined}
                zipCode={oc(location).zipCode() || undefined}
              />
            </Typography>
          </div>
        )}
      </div>
    );
  }
}
